import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cmsText } from "@/lib/cms";
import { MERKEN } from "@/lib/brands";
import { thumbUrl, marginInfo } from "@/lib/product";
import { useSchap } from "@/hooks/useSchap";
import { useMobiel } from "@/hooks/useMobiel";
import { useCategoryLabels } from "@/hooks/useCategoryLabels";
import { useAuth } from "@/context/AuthContext";
import { Cta, SegmentToggle } from "@/components/blocks/shared";

const PER_PLANK = 4;
const HANGENDE_MERKEN = ["nextbrush"];
const STANDAARD_HOOGTE_CM = { frankly: 17.5, nextbrush: 22 };
const HOOGTE_PER_CATEGORIE_CM = { gezichtsverzorging: 11.8, gezichtsreinigers: 17.5, "interdentale mondzorg": 13.5, tandenborstels: 22 };

const naamVan = (p, lang) => p.naam?.[lang] || p.naam?.nl || p.sku;
const LOCALE = { nl: "nl-NL", en: "en-IE", de: "de-DE", fr: "fr-FR" };
const kolommen = (n) => ({ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` });
/* het eurogat zit niet in het horizontale midden van de foto; nudge de foto zodat het gat op het haakje valt (+ omlaag t.o.v. de lus) */
const HANG_NUDGE = { "interdentale mondzorg": { x: "12%", y: "6px" }, tandenborstels: { x: "8%", y: "10px" } };

/* Chic prijskaartje (teal) met artikelnummer en consumentenadviesprijs; na login ook inkoopprijs + marge. */
const Prijskaartje = ({ product, lang, prijsInfo, className = "", style }) => {
  const { t } = useTranslation();
  const euro = (n) => new Intl.NumberFormat(LOCALE[lang] || "nl-NL", { style: "currency", currency: "EUR" }).format(n);
  const advies = product.adviesprijs > 0 ? euro(product.adviesprijs) : null;
  const inkoop = prijsInfo?.verkoopprijs > 0 ? euro(prijsInfo.verkoopprijs) : null;
  return (
    <span className={`prijskaartje ${className}`} style={style} data-testid={`shelf-tag-${product.sku}`}>
      <span className="prijskaartje-oog" aria-hidden="true" />
      <span className="block font-mono text-[6.5px] uppercase tracking-[0.16em] opacity-65">{product.sku}</span>
      {advies && <span className="mt-0.5 block font-display text-[11px] font-bold leading-none tracking-tight sm:text-[12px]">{advies}</span>}
      {advies && <span className="mt-0.5 block text-[5.5px] uppercase tracking-[0.14em] opacity-55">{t("shelf.rrp")}</span>}
      {inkoop && (
        <span className="mt-1 block border-t border-white/15 pt-1" data-testid={`shelf-tag-inkoop-${product.sku}`}>
          <span className="block font-display text-[10px] font-bold leading-none tracking-tight sm:text-[11px]">{inkoop}</span>
          <span className="mt-0.5 block text-[5.5px] uppercase tracking-[0.14em] opacity-60">{t("shelf.yourPrice")}</span>
          {prijsInfo.marge != null && (
            <span className="mt-1 block rounded-sm bg-white/15 px-1 py-0.5 text-center text-[6.5px] font-semibold uppercase tracking-[0.08em]" data-testid={`shelf-tag-marge-${product.sku}`}>
              {t("margin.badge", { pct: prijsInfo.marge })}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

/* Eén product als uitgeknipte packshot op ware schaal (cm → px via --cm): staand op de plank, of hangend aan een haakje (eurolock). */
const ShelfProduct = ({ product, usp, categorie, catId, lang, index, hangend, hoogteCm, prijsInfo, onOpen }) => {
  const naam = naamVan(product, lang);
  const swingStyle = {
    "--swing-dur": `${3.2 + (index % 4) * 0.45}s`,
    "--swing-delay": `${(index % 3) * 0.3}s`,
    marginTop: "calc(-1 * var(--haak) - 6px)",
  };
  const maat = `calc(var(--cm) * ${hoogteCm})`;
  const nudge = HANG_NUDGE[String(catId || "").toLowerCase()] || { x: "0px", y: "0px" };
  const foto = (
    <img
      src={thumbUrl(product.foto, 480, { alpha: true, trim: true })}
      alt={naam}
      style={{ height: maat, maxWidth: maat }}
      className={`shelf-product block w-auto object-contain ${hangend ? "shelf-product-hang object-top" : "object-bottom"}`}
    />
  );
  return (
    <Tooltip delayDuration={80}>
      <TooltipTrigger asChild>
        <motion.button
          type="button"
          onClick={() => onOpen(product)}
          initial={{ opacity: 0, y: hangend ? -16 : 24, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: hangend ? -10 : 12, scale: 0.94 }}
          transition={{ duration: 0.45, delay: index * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
          className="group relative flex flex-col items-center focus:outline-none"
          aria-label={naam}
          data-testid={`shelf-product-${product.sku}`}
        >
          {hangend ? (
            <span className="flex items-start gap-4 lg:gap-6">
              <span className="relative flex flex-col items-center">
                {/* chroom haak zit onder de balk geschroefd en steekt door het eurogat / lusje in de echte productfoto */}
                <img src="/schap/haak-draad.png" alt="" className="hook relative z-0" aria-hidden="true" />
                <span className="swing relative z-10 flex flex-col items-center" style={swingStyle}>
                  <span className="block" style={{ transform: `translate(${nudge.x}, ${nudge.y})` }}>{foto}</span>
                </span>
              </span>
              {/* kaartje hangt aan de balk, naast het product */}
              <Prijskaartje product={product} lang={lang} prijsInfo={prijsInfo} className="prijskaartje-aan-balk" />
            </span>
          ) : (
            <span className="relative">
              {foto}
              <span className="contact-shadow" aria-hidden="true" />
            </span>
          )}
        </motion.button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px] rounded-xl border-0 bg-ink px-4 py-3 text-ink-foreground shadow-xl">
        <p className="font-mono text-[10px] uppercase tracking-widest opacity-60">{categorie}</p>
        <p className="mt-1 font-display text-sm font-semibold leading-snug">{naam}</p>
        {usp && <p className="mt-1.5 text-xs leading-relaxed opacity-80">{usp}</p>}
      </TooltipContent>
    </Tooltip>
  );
};

/* Staande rij: uitgeknipte producten op een zwevende walnoten plank (fotorealistisch) met prijskaartjes aan de voorrand. */
const Plank = ({ items, kaartje, children }) => (
  <div className="relative pb-12 sm:pb-14" data-testid="shelf-plank">
    <div className="plank-row relative z-10 grid items-end justify-items-center gap-2 px-3 sm:gap-4" style={kolommen(items.length)}>{children}</div>
    <div className="plank" aria-hidden="true">
      <img src="/schap/plank.png" alt="" className="plank-img" />
      <span className="plank-shadow" />
      <div className="absolute inset-x-0 top-[56%] z-10 grid justify-items-center gap-2 px-3 sm:gap-4" style={kolommen(items.length)}>
        {items.map((p) => kaartje(p))}
      </div>
    </div>
  </div>
);

/* Hangende rij: houten balk met chroom eurolock-haken waar de producten aan bungelen. */
const Haakjesrail = ({ items, children }) => (
  <div className="relative pt-2" data-testid="shelf-hooks">
    <div className="hook-rail relative z-20 mx-1" aria-hidden="true">
      <img src="/schap/plank.png" alt="" className="hook-rail-img" />
    </div>
    <div className="relative z-10 grid items-start justify-items-center gap-2 px-3 pb-3 sm:gap-4" style={kolommen(items.length)}>{children}</div>
  </div>
);

const ProductDialog = ({ product, usp, categorie, lang, onClose }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const open = !!product;
  const naam = product ? naamVan(product, lang) : "";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl overflow-hidden rounded-2xl p-0" data-testid="shelf-product-dialog">
        {product && (
          <div className="grid sm:grid-cols-2">
            <div className="bg-secondary/60 p-6 sm:p-8">
              <img src={thumbUrl(product.foto, 720, { alpha: true })} alt={naam} className="aspect-square w-full object-contain drop-shadow-[0_24px_24px_hsl(30_20%_20%/0.3)]" />
            </div>
            <div className="flex flex-col p-6 sm:p-8">
              <DialogHeader className="text-left">
                <p className="eyebrow">{categorie}</p>
                <DialogTitle className="mt-2 font-display text-xl font-bold tracking-tight sm:text-2xl">{naam}</DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {usp || t("shelf.defaultUsp")}
                </DialogDescription>
              </DialogHeader>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                {t("shelf.sku")} {product.sku}
              </p>
              <div className="mt-auto pt-6">
                {isAuthenticated ? (
                  <Button className="w-full rounded-full" asChild data-testid="shelf-dialog-catalog-link">
                    <Link to={`/producten/${product.id}`}>
                      {t("shelf.viewInCatalog")}
                      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                ) : (
                  <>
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("shelf.pricesAfterLogin")}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <Button className="flex-1 rounded-full" asChild data-testid="shelf-dialog-register-link">
                        <Link to="/registreren">{t("landing.ctaRegister")}</Link>
                      </Button>
                      <Button variant="outline" className="flex-1 rounded-full" asChild data-testid="shelf-dialog-login-link">
                        <Link to="/login">{t("landing.ctaLogin")}</Link>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ShelfBlock = ({ block }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const T = (f) => cmsText(f, lang);
  const { producten, loading } = useSchap();
  const mobiel = useMobiel();
  const { isAuthenticated, account, producten: fetchProducten } = useAuth();
  const [prijzen, setPrijzen] = useState(null);
  const [merk, setMerk] = useState(block.standaard_merk || "frankly");
  const [geopend, setGeopend] = useState(null);
  const categorieLabel = useCategoryLabels(useMemo(() => [...new Set(producten.map((p) => p.categorie).filter(Boolean))], [producten]));

  useEffect(() => {
    if (!isAuthenticated || !fetchProducten) return;
    let actueel = true;
    fetchProducten()
      .then((lijst) => {
        if (!actueel) return;
        const map = new Map();
        (Array.isArray(lijst) ? lijst : []).forEach((p) => {
          const marge = marginInfo(p, account);
          map.set(String(p.sku).toUpperCase(), { verkoopprijs: Number(p.verkoopprijs) || 0, marge: marge?.percentage ?? null });
        });
        setPrijzen(map);
      })
      .catch(() => {});
    return () => { actueel = false; };
  }, [isAuthenticated, fetchProducten, account]);

  const usps = useMemo(() => {
    const map = new Map();
    (block.usps || []).forEach((u) => u?.sku && map.set(String(u.sku).toUpperCase(), T(u.tekst)));
    return map;
  }, [block.usps, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const hoogtes = useMemo(() => {
    const map = new Map();
    (block.usps || []).forEach((u) => u?.sku && Number(u.hoogte_cm) > 0 && map.set(String(u.sku).toUpperCase(), Number(u.hoogte_cm)));
    return map;
  }, [block.usps]);
  const hoogteVan = (p) =>
    hoogtes.get(String(p.sku).toUpperCase()) ||
    HOOGTE_PER_CATEGORIE_CM[String(p.categorie || "").toLowerCase()] ||
    Number(block[`hoogte_${p.merk}_cm`]) ||
    STANDAARD_HOOGTE_CM[p.merk] ||
    18;

  const perMerk = useMemo(() => {
    const max = Number(block.max_per_merk) || PER_PLANK * 2;
    const gekozen = String(block.skus || "").split(/[,\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
    const out = {};
    MERKEN.forEach((m) => {
      const alle = producten.filter((p) => p.merk === m.id);
      let lijst;
      if (gekozen.length) {
        lijst = gekozen.map((sku) => alle.find((p) => String(p.sku).toUpperCase() === sku)).filter(Boolean);
      } else {
        const basis = alle.filter((p) => !p.variant);
        lijst = [...(basis.length >= PER_PLANK ? basis : alle)].sort((a, b) => String(a.sku).localeCompare(String(b.sku)));
      }
      out[m.id] = lijst.slice(0, max);
    });
    return out;
  }, [producten, block.max_per_merk, block.skus]);

  const merken = MERKEN.filter((m) => perMerk[m.id]?.length).map((m) => ({ id: m.id, label: m.naam, count: perMerk[m.id].length }));
  const actief = merken.some((m) => m.id === merk) ? merk : merken[0]?.id;
  const zichtbaar = perMerk[actief] || [];
  const hangend = block.hangende_stijl !== "staand" && HANGENDE_MERKEN.includes(actief);
  // mobiel + hangend: 2 per balk zodat product én (uitgebreid) kaartje ruim naast elkaar passen
  const perPlank = mobiel && hangend ? 2 : zichtbaar.length > PER_PLANK ? Math.ceil(zichtbaar.length / 2) : PER_PLANK;
  const planken = [];
  for (let i = 0; i < zichtbaar.length; i += perPlank) planken.push(zichtbaar.slice(i, i + perPlank));
  const draaipunt = `${Number(block.haak_positie) || 12}px`; // hoogte van het eurogat t.o.v. de bovenkant van de (uitgeknipte) foto
  const uspVan = (p) => p.usp?.[lang] || p.usp?.nl || usps.get(String(p.sku).toUpperCase()) || "";
  const catVan = (p) => categorieLabel(p.categorie) || "";
  const prijsVan = (p) => prijzen?.get(String(p.sku).toUpperCase()) || null;
  const kaartje = (p) => <Prijskaartje key={p.id} product={p} lang={lang} prijsInfo={prijsVan(p)} />;

  return (
    <section className="relative overflow-hidden border-b bg-background" data-testid="block-schap">
      <div className="absolute inset-0 hero-noise" aria-hidden="true" />
      <div className="absolute -left-40 top-10 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-24 lg:pt-20">
        <div className="min-w-0 lg:col-span-5">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.6 }}
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {T(block.titel)}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16, duration: 0.6 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            {T(block.subtitel)}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Cta label={T(block.cta_label)} link={block.cta_link} testId="block-schap-cta" arrow />
            <Cta label={T(block.cta2_label)} link={block.cta2_link} variant="outline" testId="block-schap-cta2" />
          </motion.div>
        </div>

        <div className="min-w-0 lg:col-span-7" data-testid="shelf">
          <div className="mb-6 flex flex-wrap items-center justify-end gap-3">
            {merken.length > 1 && <SegmentToggle options={merken} value={actief} onChange={setMerk} testIdPrefix="brand-toggle" />}
          </div>
          <div className="shelf-scale relative px-1 pb-2 pt-2 sm:px-3" style={{ "--haak": draaipunt }} data-testid="shelf-unit">
            {loading ? (
              <div className="space-y-12 py-6">
                {[0, 1].map((i) => (
                  <div key={i} className="flex items-end justify-evenly gap-4">
                    {Array.from({ length: PER_PLANK }).map((_, j) => <Skeleton key={j} className="aspect-[3/4] w-20 rounded-lg sm:w-24 lg:w-28" />)}
                  </div>
                ))}
              </div>
            ) : (
              <TooltipProvider>
              <AnimatePresence mode="wait">
                <motion.div key={actief} className="space-y-6 pt-4 sm:space-y-8" initial={false}>
                  {planken.map((plank, pi) =>
                    hangend ? (
                      <Haakjesrail key={pi} items={plank}>
                        {plank.map((p, i) => (
                          <ShelfProduct key={p.id} product={p} index={pi * perPlank + i} usp={uspVan(p)} categorie={catVan(p)} catId={p.categorie} lang={lang} hoogteCm={hoogteVan(p)} prijsInfo={prijsVan(p)} hangend onOpen={setGeopend} />
                        ))}
                      </Haakjesrail>
                    ) : (
                      <Plank key={pi} items={plank} kaartje={kaartje}>
                        {plank.map((p, i) => (
                          <ShelfProduct key={p.id} product={p} index={pi * perPlank + i} usp={uspVan(p)} categorie={catVan(p)} lang={lang} hoogteCm={hoogteVan(p)} onOpen={setGeopend} />
                        ))}
                      </Plank>
                    ),
                  )}
                  {planken.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">{t("shelf.empty")}</p>}
                </motion.div>
              </AnimatePresence>
            </TooltipProvider>
            )}
          </div>
        </div>
      </div>
      <ProductDialog product={geopend} usp={geopend ? uspVan(geopend) : ""} categorie={geopend ? catVan(geopend) : ""} lang={lang} onClose={() => setGeopend(null)} />
    </section>
  );
};
